import {
	type ComponentProps,
	type FC,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Button, type ButtonProps } from "./Button";
import "./SearchBox.css";
import cn from "classnames";
import { Spinner } from "../common/Spinner";

export type AutocompleteProps = {
	onClick: () => void;
};

interface Props extends ComponentProps<"div"> {
	inputProps: ComponentProps<typeof ExtraForm.Control>;
	autocompleteItems?: ButtonProps[];
	loading?: boolean;
}

const SearchBox: FC<Props> = ({
	inputProps,
	autocompleteItems,
	loading,
	className,
	...rest
}) => {
	const id = useId();
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [afterHalf, setAfterHalf] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			if (!wrapperRef.current) {
				return;
			}

			const { y, height } = wrapperRef.current.getBoundingClientRect();
			const winHeight = window.innerHeight;

			if (y + height > winHeight / 2) {
				setAfterHalf(true);
				return;
			}
			setAfterHalf(false);
		};

		window.addEventListener("scroll", handleScroll);
		handleScroll();

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	return (
		<div
			className={cn(
				"searchbox",
				{ top: afterHalf },
				{ open: autocompleteItems?.length ?? loading },
				className,
			)}
			ref={wrapperRef}
			{...rest}
		>
			<ExtraForm.Control {...inputProps} type="search" />
			{(autocompleteItems?.length || loading) && (
				<ul>
					{autocompleteItems?.map((item, idx) => {
						const { children, ...buttonRest } = item;
						return (
							<li
								key={`autocomplete-${id}-${
									// biome-ignore lint/suspicious/noArrayIndexKey: prefixed
									idx
								}`}
							>
								<Button {...buttonRest}>{children}</Button>
							</li>
						);
					})}
					<li>{loading && <Spinner size={16} />}</li>
				</ul>
			)}
		</div>
	);
};

export default SearchBox;
